import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { 
  ArrowLeft,
  Search,
  BookOpen,
  User,
  Briefcase,
  Settings,
  FileText,
  Target,
  Shield,
  Bell,
  MessageSquare,
  HelpCircle,
  ChevronRight,
  Sparkles,
  CheckCircle,
  Users,
  TrendingUp,
  Mail
} from 'lucide-react';

// Knowledge Base Content
const KB_SECTIONS = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: BookOpen,
    color: 'bg-lime-100 text-lime-700',
    articles: [
      {
        id: 'what-is-varyynt',
        title: 'What is Varyynt?',
        content: `Varyynt is an AI-powered career matching platform designed specifically for life sciences professionals. Unlike traditional job boards where you search for jobs, Varyynt works the other way around — you tell us what you're looking for, and we match opportunities to you.

**Key Differences:**
• **Candidate-First:** Your profile is never visible to employers. You control when and how you engage.
• **Future-Focused:** We match based on where you want to go, not just where you've been.
• **Life Sciences Specialized:** Built specifically for pharma, biotech, medical devices, and digital health.
• **V-FIT Scoring:** Our proprietary algorithm ranks jobs based on your unique preferences and aspirations.`
      },
      {
        id: 'how-matching-works',
        title: 'How does job matching work?',
        content: `Our V-FIT (Varyynt Future-Intent Trajectory) scoring system evaluates each job against your profile on multiple dimensions:

**Scoring Components:**
• **Skills Alignment (20 pts):** How well your skills match the role requirements
• **Functional Fit (20 pts):** Alignment with your preferred job functions
• **Seniority Trajectory (20 pts):** Whether the role matches your career progression goals
• **Industry Match (10 pts):** Relevance to your preferred industries
• **Company Size (10 pts):** Match with your company size preferences
• **Compensation (10 pts):** Alignment with your salary expectations
• **Location/Work Style (10 pts):** Remote, hybrid, or onsite preferences

**Match Strength Categories:**
• **Strong (80-100):** Excellent fit — these should be at the top of your list
• **Clear (60-79):** Good alignment — worth exploring
• **Emerging (40-59):** Potential opportunity — review to see if it fits`
      },
      {
        id: 'completing-profile',
        title: 'Why should I complete my profile?',
        content: `Your profile completion directly impacts the quality of your job matches. Here's why each section matters:

**About You:** Helps us understand your current position and experience level
**Industries & Functions:** Defines which opportunities we'll show you
**Company Preferences:** Filters out companies that don't match your work style
**Work Style:** Ensures we only show remote, hybrid, or onsite roles you want
**Compensation:** Prevents wasting time on roles outside your range
**Deal Makers & Breakers:** Critical filters that eliminate poor-fit opportunities
**Disease Areas:** Especially important for life sciences — your therapeutic expertise
**Resume:** Provides context for skills matching and enriches your profile

**Pro Tip:** Profiles at 100% completion receive significantly better matches because we have more data to work with.`
      }
    ]
  },
  {
    id: 'your-profile',
    title: 'Your Profile',
    icon: User,
    color: 'bg-blue-100 text-blue-700',
    articles: [
      {
        id: 'editing-profile',
        title: 'How do I edit my profile?',
        content: `You can edit your profile in several ways:

**From the Dashboard:**
1. Click "View My Profile" to see your current profile
2. Click "Edit Full Profile" at the bottom to enter the onboarding wizard
3. Or click on any section card to jump directly to that section

**From the Profile Page:**
1. Hover over any preference card to reveal the edit button
2. Click to jump directly to that section in the onboarding wizard
3. Make your changes and they'll save automatically

**Tip:** Changes to your profile automatically trigger a re-sync with our matching system, so your job matches will update to reflect your new preferences.`
      },
      {
        id: 'ai-summary',
        title: 'What is the AI-generated summary?',
        content: `The AI summary on your profile page is a first-person narrative that summarizes your career identity. It's generated using your profile data to create a cohesive story about who you are professionally.

**Features:**
• Automatically generated based on your profile data
• Written in first person for a personal touch
• Can be edited if you want to customize it
• Can be regenerated if you make significant profile changes

**Note:** This summary is for your reference only — it's not shared with employers.`
      }
    ]
  },
  {
    id: 'job-matches',
    title: 'Job Matches',
    icon: Target,
    color: 'bg-amber-100 text-amber-700',
    articles: [
      {
        id: 'understanding-vfit',
        title: 'Understanding your V-FIT score',
        content: `The V-FIT score (0-100) represents how well a job aligns with your profile and preferences.

**Score Breakdown:**
Each match shows the specific reasons it scored well for you. Common reasons include:
• Skills that match the role requirements
• Industry alignment with your preferences
• Compensation within your target range
• Location/work style compatibility
• Company size and stage preferences

**What to Do:**
• **80+ (Strong):** These deserve immediate attention
• **60-79 (Clear):** Worth reviewing — could be great opportunities
• **40-59 (Emerging):** May have potential — review the details

**Important:** V-FIT scores are guidance, not guarantees. Always review the full job details before deciding to apply.`
      },
      {
        id: 'applying-jobs',
        title: 'How do I apply to jobs?',
        content: `When you find a job you're interested in:

1. **Click on the job card** to open the full details
2. **Review the match reasons** to understand why it was recommended
3. **Check the job details** (salary, location, requirements)
4. **Click "Apply Now"** to be taken directly to the application

**Note:** Clicking "Apply Now" opens the employer's application page in a new tab. Your Varyynt profile is NOT automatically shared — you'll apply through their standard process.

**Tip:** Jobs are active listings that may be filled at any time. If you see a strong match, don't wait too long to apply.`
      }
    ]
  },
  {
    id: 'privacy-security',
    title: 'Privacy & Security',
    icon: Shield,
    color: 'bg-purple-100 text-purple-700',
    articles: [
      {
        id: 'data-privacy',
        title: 'How is my data protected?',
        content: `Your privacy is fundamental to how Varyynt works:

**What We Never Do:**
• Never sell your personal data
• Never allow employers to browse or search candidate profiles
• Never share your information without your explicit action

**What We Do:**
• Encrypt all personal data
• Use your profile only for matching purposes
• Allow you to delete your account and data at any time
• Process data in accordance with our Privacy Policy

**Your Control:**
• Your profile is invisible to employers
• You decide when to apply to a job
• You can export or delete your data from Account Settings`
      },
      {
        id: 'account-security',
        title: 'Account security best practices',
        content: `Keep your account secure:

**Password:**
• Use at least 8 characters
• Include a mix of letters, numbers, and symbols
• Don't reuse passwords from other sites

**Email:**
• Keep your email address up to date
• This is how we send important notifications
• Used for account recovery if needed

**If You Suspect Unauthorized Access:**
1. Change your password immediately
2. Contact our support team
3. Review your account activity`
      }
    ]
  },
  {
    id: 'notifications',
    title: 'Notifications',
    icon: Bell,
    color: 'bg-teal-100 text-teal-700',
    articles: [
      {
        id: 'notification-settings',
        title: 'Managing notification preferences',
        content: `Control how and when we contact you:

**Email Frequency Options:**
• **Immediate:** Get notified as soon as a matching role appears
• **Weekly Digest:** Receive a consolidated email every week
• **Bi-weekly Digest:** Receive a consolidated email every two weeks
• **In-app Only:** No emails — view matches in your dashboard only

**To Change Settings:**
1. Go to Account Settings
2. Find the "Notification Preferences" section
3. Select your preferred frequency
4. Changes save automatically

**In-App Notifications:**
• Always enabled regardless of email preferences
• Check the bell icon in the navigation for updates
• Includes platform announcements and new messages`
      }
    ]
  },
  {
    id: 'support',
    title: 'Getting Help',
    icon: MessageSquare,
    color: 'bg-red-100 text-red-700',
    articles: [
      {
        id: 'contact-support',
        title: 'How do I contact support?',
        hasAction: true,
        actionLabel: 'Open Support Ticket',
        actionLink: '/support',
        content: `We're here to help!

**Submit a Support Request:**
1. Click the "Open Support Ticket" button below
2. Fill out the form with your question
3. Our team will respond via email

**What to Include:**
• A clear description of your issue or question
• Any relevant details (what you were trying to do, what happened)
• Screenshots if applicable

**Response Time:**
We aim to respond to all support requests within 24-48 business hours.

**For Urgent Issues:**
If you're experiencing a security concern or account access issue, please note that in your support request and we'll prioritize accordingly.`
      }
    ]
  }
];

// Search helper
const searchArticles = (query) => {
  if (!query.trim()) return [];
  const lowerQuery = query.toLowerCase();
  const results = [];
  
  KB_SECTIONS.forEach(section => {
    section.articles.forEach(article => {
      if (
        article.title.toLowerCase().includes(lowerQuery) ||
        article.content.toLowerCase().includes(lowerQuery)
      ) {
        results.push({ ...article, sectionTitle: section.title, sectionId: section.id });
      }
    });
  });
  
  return results;
};

// Article Card Component
const ArticleCard = ({ article, onClick }) => {
  // Strip markdown formatting from preview
  const cleanPreview = article.content
    .replace(/\*\*(.*?)\*\*/g, '$1')  // Remove bold markers
    .replace(/\n/g, ' ')              // Replace newlines with spaces
    .replace(/•/g, '')                // Remove bullet points
    .replace(/\s+/g, ' ')             // Collapse multiple spaces
    .trim()
    .substring(0, 120);

  return (
    <button
      onClick={onClick}
      className="w-full text-left p-4 rounded-lg border border-slate-200 hover:border-lime-300 hover:bg-lime-50/50 transition-all group"
    >
      <div className="flex items-start justify-between">
        <div>
          <h4 className="font-medium text-slate-900 group-hover:text-lime-700 transition-colors">
            {article.title}
          </h4>
          <p className="text-sm text-slate-500 mt-1 line-clamp-2">
            {cleanPreview}...
          </p>
        </div>
        <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-lime-500 transition-colors flex-shrink-0" />
      </div>
    </button>
  );
};

// Article View Component
const ArticleView = ({ article, onBack, navigate }) => (
  <div className="animate-slideUp">
    <Button variant="ghost" size="sm" onClick={onBack} className="mb-4">
      <ArrowLeft className="h-4 w-4 mr-2" />
      Back to Help Center
    </Button>
    
    <Card>
      <CardHeader>
        <CardTitle>{article.title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="prose prose-slate max-w-none">
          {article.content.split('\n\n').map((paragraph, idx) => {
            if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
              return <h3 key={idx} className="text-lg font-semibold text-slate-900 mt-6 mb-3">{paragraph.replace(/\*\*/g, '')}</h3>;
            }
            if (paragraph.startsWith('•')) {
              const items = paragraph.split('\n').filter(line => line.startsWith('•'));
              return (
                <ul key={idx} className="space-y-2 my-4">
                  {items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-600">
                      <CheckCircle className="h-4 w-4 text-lime-500 mt-1 flex-shrink-0" />
                      <span dangerouslySetInnerHTML={{ __html: item.substring(2).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                    </li>
                  ))}
                </ul>
              );
            }
            return <p key={idx} className="text-slate-600 my-4" dangerouslySetInnerHTML={{ __html: paragraph.replace(/\*\*(.*?)\*\*/g, '<strong class="text-slate-900">$1</strong>') }} />;
          })}
        </div>
        
        {/* Action Button for articles with actions */}
        {article.hasAction && article.actionLink && (
          <div className="mt-8 pt-6 border-t border-slate-200">
            <Button 
              onClick={() => navigate(article.actionLink)}
              className="bg-evergreen-950 hover:bg-evergreen-900"
              data-testid="article-action-btn"
            >
              <Mail className="h-4 w-4 mr-2" />
              {article.actionLabel || 'Take Action'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  </div>
);

export default function HelpCenterPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [expandedSection, setExpandedSection] = useState('getting-started');

  const searchResults = searchArticles(searchQuery);

  if (selectedArticle) {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-2">
                <img src="/varyynt-logo.svg" alt="Varyynt" className="h-10" />
              </Link>
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
                Dashboard
              </Button>
            </div>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ArticleView article={selectedArticle} onBack={() => setSelectedArticle(null)} navigate={navigate} />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/varyynt-logo.svg" alt="Varyynt" className="h-10" />
            </Link>
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
                Dashboard
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-br from-evergreen-950 to-evergreen-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-lime-500/20 mb-6">
              <HelpCircle className="h-8 w-8 text-lime-400" />
            </div>
            <h1 className="text-3xl font-heading mb-3">Help Center</h1>
            <p className="text-white/70 max-w-xl mx-auto mb-8">
              Find answers to common questions and learn how to get the most out of Varyynt
            </p>
            
            {/* Search */}
            <div className="relative max-w-lg mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search for help..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 bg-white text-slate-900"
                data-testid="help-search-input"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Results */}
        {searchQuery && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">
              {searchResults.length > 0 
                ? `${searchResults.length} result${searchResults.length === 1 ? '' : 's'} for "${searchQuery}"`
                : `No results for "${searchQuery}"`
              }
            </h2>
            {searchResults.length > 0 ? (
              <div className="space-y-3">
                {searchResults.map((article) => (
                  <ArticleCard
                    key={article.id}
                    article={article}
                    onClick={() => setSelectedArticle(article)}
                  />
                ))}
              </div>
            ) : (
              <p className="text-slate-500">Try different keywords or browse the categories below.</p>
            )}
          </div>
        )}

        {/* Categories */}
        {!searchQuery && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
              {KB_SECTIONS.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setExpandedSection(section.id)}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      expandedSection === section.id 
                        ? 'border-lime-500 bg-lime-50' 
                        : 'border-slate-200 hover:border-slate-300 bg-white'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg ${section.color} flex items-center justify-center mb-3`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="font-medium text-slate-900">{section.title}</h3>
                    <p className="text-sm text-slate-500 mt-1">{section.articles.length} articles</p>
                  </button>
                );
              })}
            </div>

            {/* Expanded Section Articles */}
            {expandedSection && (
              <Card className="animate-slideUp">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {(() => {
                      const section = KB_SECTIONS.find(s => s.id === expandedSection);
                      const Icon = section?.icon || BookOpen;
                      return (
                        <>
                          <Icon className="h-5 w-5 text-lime-600" />
                          {section?.title}
                        </>
                      );
                    })()}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {KB_SECTIONS.find(s => s.id === expandedSection)?.articles.map((article) => (
                      <ArticleCard
                        key={article.id}
                        article={article}
                        onClick={() => setSelectedArticle(article)}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Contact Support CTA */}
        <Card className="mt-8 border-lime-200 bg-gradient-to-br from-lime-50 to-emerald-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-lime-100 flex items-center justify-center flex-shrink-0">
                <MessageSquare className="h-6 w-6 text-lime-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900">Can't find what you're looking for?</h3>
                <p className="text-sm text-slate-600 mt-1">Our support team is here to help with any questions.</p>
              </div>
              <Button 
                onClick={() => navigate('/support')}
                className="bg-evergreen-950 hover:bg-evergreen-900"
                data-testid="contact-support-btn"
              >
                <Mail className="h-4 w-4 mr-2" />
                Contact Support
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
